import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {MinMaxSeries} from "../../lq_js_common/e_charts/min_max_median_series";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatBitsPerSecondAxis,
    formatTimeTick,
    throughputUnitToBitsPerSecondMultiplier,
} from "../../helpers/chart_common";
import {debugLog} from '../../helpers/debug_log';
import {attachShowLimitsToggle, showLimitsEnabled} from "../../helpers/show_limits";

const SHOW_LIMITS_STORAGE_KEY = "lqos_circuit_throughput_show_limits";

export class ShaperCircuitThroughput extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.lastHeaderState = null;
        this.showLimits = showLimitsEnabled(SHOW_LIMITS_STORAGE_KEY);
    }

    title() {
        return "Circuit Throughput";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Circuit Throughput</h5><p>Bandwidth use (speed) during the specified time period.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.lastGraphState !== null && this.lastHeaderState !== null;
    }

    explain() {
        if (!this.isExplainReady()) return null;
        const data = this.lastGraphState || {};
        const limits = this.lastHeaderState || {};
        const throughputMultiplier = throughputUnitToBitsPerSecondMultiplier(data.throughput_unit);
        const downLimitBps = Number.isFinite(limits.download_max_mbps) ? limits.download_max_mbps * 1_000_000 : null;
        const upLimitBps = Number.isFinite(limits.upload_max_mbps) ? limits.upload_max_mbps * 1_000_000 : null;

        const descs = [];
        const downDesc = summarizeCapacity("Download", downLimitBps, data.download_bytes_max || data.download_bytes_median || [], throughputMultiplier);
        const upDesc = summarizeCapacity("Upload", upLimitBps, data.upload_bytes_max || data.upload_bytes_median || [], throughputMultiplier);
        if (downDesc) descs.push(downDesc);
        if (upDesc) descs.push(upDesc);
        if (descs.length === 0) return null;

        return { dashlet_name: this.title(), description: descs.join(" ") };
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        attachShowLimitsToggle(base, this.id, SHOW_LIMITS_STORAGE_KEY, (checked) => {
            this.showLimits = checked;
            if (this.graph && this.lastGraphState !== null && this.lastHeaderState !== null) {
                this.graph.update(this.lastGraphState, this.lastHeaderState, checked);
            }
        });
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperCircuitThroughputGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState, this.lastHeaderState, this.showLimits);
        }
    }

    channelRequests() {
        return [ "CircuitDetail", "CircuitHeader" ];
    }

    applyRenderState() {
        let myDiv = document.getElementById(this.id);
        if (!myDiv) {
            return;
        }
        if (!this.rendered) {
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        this.rendered = true;
    }

    onMessage(msg) {
        debugLog(msg);
        if (msg.Data.target === "circuit_header") {
            this.lastHeaderState = msg.Data.blob || null;
            if (this.lastGraphState) {
                this.applyRenderState();
                this.graph.update(this.lastGraphState, this.lastHeaderState, this.showLimits);
            }
            return;
        }
        if (msg.Data.target !== "circuit_detail") return;

        for (const [key, value] of Object.entries(msg.Data.blob)) {
            this.lastGraphState = value;
            if (this.lastHeaderState) {
                this.applyRenderState();
                this.graph.update(value, this.lastHeaderState, this.showLimits);
            }
        }
    }

}

class ShaperCircuitThroughputGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Bandwidth", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data, limits, showLimits = true) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.legend = { show: false, data: [] };
        const throughputMultiplier = throughputUnitToBitsPerSecondMultiplier(data?.throughput_unit);

        let down = new MinMaxSeries("Download", 0);
        let up = new MinMaxSeries("Upload", 0);
        applySplitDirectionAxes(this.option, {
            ticks: data.ticks || [],
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatBitsPerSecondAxis,
        });
        down.series.forEach((series) => {
            series.xAxisIndex = 0;
            series.yAxisIndex = 0;
            series.showSymbol = false;
        });
        up.series.forEach((series) => {
            series.xAxisIndex = 1;
            series.yAxisIndex = 0;
            series.showSymbol = false;
            series.z = 4;
        });

        const downloadLimitMbps = limits?.download_max_mbps;
        const uploadLimitMbps = limits?.upload_max_mbps;
        const downloadLimitBps = Number.isFinite(downloadLimitMbps) ? downloadLimitMbps * 1_000_000 : null;
        const uploadLimitBps = Number.isFinite(uploadLimitMbps) ? uploadLimitMbps * 1_000_000 : null;

        if (showLimits && downloadLimitBps && downloadLimitBps > 0) {
            down.series[0].markLine = {
                silent: true,
                symbol: 'none',
                label: { formatter: `Max Download (${formatBitsPerSecondAxis(downloadLimitBps)})`, position: 'insideEndTop', color: '#ff9d9d', textBorderColor: 'rgba(30,30,30,0.92)' },
                lineStyle: { type: 'dashed', opacity: 0.7, color: '#ff0000' },
                data: [{ yAxis: downloadLimitBps }],
            };
        }
        if (showLimits && uploadLimitBps && uploadLimitBps > 0) {
            up.series[0].markLine = {
                silent: true,
                symbol: 'none',
                label: { formatter: `Max Upload (${formatBitsPerSecondAxis(uploadLimitBps)})`, position: 'insideEndBottom', color: '#ff9d9d', textBorderColor: 'rgba(30,30,30,0.92)' },
                lineStyle: { type: 'dashed', opacity: 0.7, color: '#ff0000' },
                data: [{ yAxis: 0 - uploadLimitBps }],
            };
        }
        debugLog("LIMITS", down);

        debugLog(data);
        let site = data;

        for (let x=0; x<site.ticks.length; x++) {
            down.pushPositive(
                site.download_bytes_median[x] * throughputMultiplier,
                site.download_bytes_min[x] * throughputMultiplier,
                site.download_bytes_max[x] * throughputMultiplier,
            );
            up.pushNegative(
                site.upload_bytes_median[x] * throughputMultiplier,
                site.upload_bytes_min[x] * throughputMultiplier,
                site.upload_bytes_max[x] * throughputMultiplier,
            );
        }
        down.addToOptions(this.option);
        up.addToOptions(this.option);
        debugLog(this.option);
        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatBitsPerSecondAxis,
            formatter: (params) => {
                if (!params || params.length === 0) return "";
                const timeIndex = params[0].dataIndex;
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Download</strong><br/>
&nbsp;&nbsp;Min: ${formatBitsPerSecondAxis(site.download_bytes_min[timeIndex] * throughputMultiplier)}<br/>
&nbsp;&nbsp;Median: ${formatBitsPerSecondAxis(site.download_bytes_median[timeIndex] * throughputMultiplier)}<br/>
&nbsp;&nbsp;Max: ${formatBitsPerSecondAxis(site.download_bytes_max[timeIndex] * throughputMultiplier)}<br/>
<strong>Upload</strong><br/>
&nbsp;&nbsp;Min: ${formatBitsPerSecondAxis(site.upload_bytes_min[timeIndex] * throughputMultiplier)}<br/>
&nbsp;&nbsp;Median: ${formatBitsPerSecondAxis(site.upload_bytes_median[timeIndex] * throughputMultiplier)}<br/>
&nbsp;&nbsp;Max: ${formatBitsPerSecondAxis(site.upload_bytes_max[timeIndex] * throughputMultiplier)}
</div>`;
            },
        });

        finalizeSplitDirectionChart(this.option);
        this.chart.setOption(this.option, true);
    }
}

function summarizeCapacity(label, limitBps, seriesThroughput, throughputMultiplier) {
    if (!Number.isFinite(limitBps) || limitBps <= 0) return null;
    const values = Array.isArray(seriesThroughput) ? seriesThroughput : [];
    let total = 0;
    let approach = 0;
    let over = 0;
    values.forEach(v => {
        if (!Number.isFinite(v)) return;
        total++;
        const ratio = (v * throughputMultiplier) / limitBps;
        if (ratio >= 1.0) over++;
        if (ratio >= 0.9) approach++;
    });
    if (total === 0 || approach === 0) return null;
    const approachPct = Math.round((approach / total) * 100);
    const overPct = Math.round((over / total) * 100);
    let desc = `${label} approached capacity in ${approachPct}% of samples`;
    if (over > 0) {
        desc += ` (over limit in ${overPct}%).`;
    } else {
        desc += ".";
    }
    return desc;
}
